import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import SmartMenu from "@/components/SmartMenu";
import CompanyCodeModal from "@/components/CompanyCodeModal";
import DesktopNav from "@/components/nav/DesktopNav";
import { MobileNavToggle, MobileNavPanel } from "@/components/nav/MobileNav";
import useUserMenuItems from "@/components/nav/useUserMenuItems";
import { buildSupportHref, openSupportEmail, supportEmail } from "@/components/nav/support";
import {
  Moon, Sun, HardHat, Users, Briefcase, UploadCloud,
  Menu, X, MessageCircleQuestion,
} from "lucide-react";

// Base tabs — Import is admin-only and added conditionally below.
const BASE_NAV_ITEMS = [
  { to: "/dashboard", label: "Roster",    Icon: Users,       testId: "nav-roster" },
  { to: "/jobs",      label: "Job Sites", Icon: Briefcase,   testId: "nav-jobs" },
];
const IMPORT_ITEM = { to: "/import", label: "Import", Icon: UploadCloud, testId: "nav-import" };

export default function Navbar() {
  const { user } = useAuth();
  const { theme, toggle } = useTheme();
  const [navOpen, setNavOpen] = useState(false);
  const [showCode, setShowCode] = useState(false);

  const openCompanyCode = useCallback(() => setShowCode(true), []);
  const userMenuItems = useUserMenuItems({ onOpenCompanyCode: openCompanyCode });

  const navItems = useMemo(() => {
    if (!user || user.role === "employee") return [];
    return user.role === "admin" ? [...BASE_NAV_ITEMS, IMPORT_ITEM] : BASE_NAV_ITEMS;
  }, [user]);

  const hasMobileNav = navItems.length > 0;

  return (
    <header className="sticky top-0 z-40 border-b-2 border-foreground dark:border-safety-yellow bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-3">
        {/* Logo + mobile hamburger */}
        <div className="flex items-center gap-2 min-w-0">
          <Link to="/dashboard" data-testid="nav-logo" className="flex items-center gap-2 min-w-0">
            <span className="inline-flex items-center justify-center h-10 w-10 bg-safety-yellow text-black border-2 border-black dark:border-safety-yellow flex-shrink-0">
              <HardHat className="w-5 h-5" strokeWidth={3} />
            </span>
            <span className="font-heading text-xl sm:text-2xl leading-none truncate">
              KARM<span className="text-safety-yellow">.</span>SAFETY
            </span>
          </Link>
          {hasMobileNav && (
            <MobileNavToggle open={navOpen} onToggle={() => setNavOpen((v) => !v)} />
          )}
        </div>

        <DesktopNav items={navItems} />

        {/* Support / theme / user menu */}
        <div className="flex items-center gap-2">
          <a
            href={buildSupportHref()}
            onClick={openSupportEmail}
            data-testid="support-btn"
            aria-label="Contact support"
            title={`Email ${supportEmail}`}
            className="h-12 w-12 inline-flex items-center justify-center border-2 border-foreground dark:border-safety-yellow text-foreground hover:bg-safety-yellow hover:text-black transition-all duration-150 hover:opacity-90 active:translate-x-[1px] active:translate-y-[1px]"
          >
            <MessageCircleQuestion className="w-5 h-5" strokeWidth={2.5} />
          </a>
          <button
            data-testid="theme-toggle"
            onClick={toggle}
            aria-label="Toggle theme"
            className="h-12 w-12 inline-flex items-center justify-center border-2 border-foreground dark:border-safety-yellow hover:bg-safety-yellow hover:text-black transition-colors"
          >
            {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
          {user && (
            <SmartMenu
              testId="user-menu"
              items={userMenuItems}
              menuWidth={240}
              renderTrigger={({ ref, onClick, open, testId }) => (
                <button
                  ref={ref}
                  type="button"
                  data-testid={testId}
                  onClick={onClick}
                  aria-label={open ? "Close menu" : "Open menu"}
                  aria-expanded={open}
                  aria-haspopup="menu"
                  className={`h-12 w-12 inline-flex items-center justify-center border-2 border-black transition-colors ${
                    open
                      ? "bg-safety-yellowHover text-black"
                      : "bg-safety-yellow text-black hover:bg-safety-yellowHover"
                  }`}
                >
                  {open ? <X className="w-5 h-5" strokeWidth={3} /> : <Menu className="w-5 h-5" strokeWidth={3} />}
                </button>
              )}
            />
          )}
        </div>
      </div>

      {hasMobileNav && navOpen && (
        <MobileNavPanel items={navItems} onClose={() => setNavOpen(false)} />
      )}

      {showCode && <CompanyCodeModal onClose={() => setShowCode(false)} />}
    </header>
  );
}
